import { useAuthStore } from '@/stores/auth.store.ts'

/**
 * Determines whether an HTTP request is eligible for token refresh.
 *
 * This helper function is needed to prevent infinite loops during authentication flows.
 * It checks if a request should trigger automatic token refresh when receiving a 401 response,
 * by excluding authentication endpoints that shouldn't trigger refresh attempts:
 * - `/auth/local` - Initial login endpoint
 * - `/auth/token/refresh` - The refresh endpoint itself
 * - `/auth/{provider}/callback` - OAuth callback endpoints
 *
 * @param params - The request parameters to check
 * @param params.url - The request URL (relative or absolute)
 * @param params.baseURL - Optional base URL to resolve relative URLs against (defaults to window.location.origin)
 * @returns `true` if the request can trigger token refresh, `false` if it should be excluded or if the URL is invalid
 */
export function isRefreshableRequest({
    url,
    baseURL,
}: {
    url?: string
    baseURL?: string
}): boolean {
    if (!url) return false

    try {
        const { pathname } = new URL(url, baseURL ?? window.location.origin)

        return !(
            pathname === '/auth/local' ||
            pathname === '/auth/token/refresh' ||
            /^\/auth\/[^/]+\/callback$/.test(pathname)
        )
    } catch {
        return false
    }
}

export function isAuthenticated() {
    return useAuthStore().user !== null
}

export function isCurrentUserVerified() {
    return !!useAuthStore().user?.emailVerifiedAt
}
